import { Grid3x3, MoreVertical, Pencil, Power, Trash2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip } from '@/components/ui/tooltip'
import type { Role } from '../types'
import type { RoleCommands } from './useRoleCommands'

type Props = {
  role: Role
  canWrite: boolean
  commands: RoleCommands
  onEdit: (role: Role) => void
  onAssign: (role: Role) => void
}

export function RoleRowActions({ role, canWrite, commands, onEdit, onAssign }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
      {canWrite ? (
        <Tooltip side="top" content={t('access.roles.actions.edit')}>
          <Button size="icon" variant="ghost" className="size-8" aria-label={t('access.roles.actions.edit')} onClick={() => onEdit(role)}>
            <Pencil />
          </Button>
        </Tooltip>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" className="size-8" aria-label={t('access.roles.actions.more')}>
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => void navigate(`/access/roles/${role.id}`)}>
            <Grid3x3 />
            {t('access.roles.actions.openDetail')}
          </DropdownMenuItem>
          {canWrite ? (
            <>
              <DropdownMenuItem onSelect={() => onAssign(role)}>
                <UserPlus />
                {t('access.roles.actions.assignUsers')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={commands.busy} onSelect={() => void commands.toggleStatus(role)}>
                <Power />
                {t(role.status === 'active' ? 'access.roles.actions.deactivate' : 'access.roles.actions.activate')}
              </DropdownMenuItem>
              {!role.isSystem ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive data-[highlighted]:bg-destructive/10 [&_svg]:text-destructive" onSelect={() => commands.requestDelete(role)}>
                    <Trash2 />
                    {t('access.roles.actions.delete')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
